<template>
  <div class="page-layout">
    <header class="page-layout__header">
      <v-container>
        <v-layout
          align-center
          wrap
        >
          <a :href="getHomeUrl()">
            <img
              class="page-layout__header-logo"
              :src="require('@/assets/svg/logo.svg')"
              alt=""
            >
          </a>
          <v-spacer />
          <div class="page-layout__header-actions">
            <slot name="header-actions" />
            <v-btn
              v-if="!checkUserRole('student')"
              variant="outlined"
              color="light"
              class="ml-2"
              @click.native="openAuthModal('login')"
            >
              Sign In
            </v-btn>
            <template v-else>
              <v-btn
                variant="outlined"
                color="light"
                class="ml-2"
                @click.native="openProfile"
              >
                My Profile
              </v-btn>
              <v-btn
                variant="outlined"
                color="light"
                class="ml-2"
                @click.native="logout()"
              >
                Log Out
              </v-btn>
            </template>
          </div>
        </v-layout>
      </v-container>
    </header>

    <template v-if="loaded">
      <section class="page-layout__intro">
        <template v-if="!showGallery">
          <media-view
            :media="media[0]"
            active
          />
          <page-layout-intro-text
            :title="title"
            :text="text"
            :button-text="buttonText"
            :place-text-bottom="placeTextBottom"
            @action="$emit('action')"
          />
        </template>

        <swiper
          v-else
          ref="mySwiper"
          :options="swiperOptions"
          class="intro-carousel"
          autoplay
        >
          <swiper-slide
            v-for="(mediaItem, idx) in media"
            :key="idx"
          >
            <media-view
              :media="mediaItem"
              :active="currentSlide === idx + 1"
            />
          </swiper-slide>
            <div
              class="swiper-pagination"
            />
            <div
              class="swiper-button-prev"

              @click.native="$refs.mySwiper.$el.swiper.slidePrev()"
            />
            <div
              class="swiper-button-next"

              @click.native="$refs.mySwiper.$el.swiper.slideNext()"
            />
          <page-layout-intro-text

              :title="title"
              :text="text"
              :button-text="buttonText"
              :place-text-bottom="placeTextBottom"
              @action="$emit('action')"
            />
        </swiper>
      </section>
      <section class="page-layout__content">
        <v-container>
          <slot />
        </v-container>
      </section>
    </template>

    <v-layout
      v-else
      align-center
      justify-center
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-layout>

    <footer
      v-if="organization"
      class="page-layout__footer"
    >
      <v-container>
        <v-layout
          align-center
          wrap
        >
          <div class="copy">
            Powered by <a
              href="https://www.studenthousingsoftware.com/"
              target="_blank"
            >roomchoice.com</a> &copy; 2014-{{ (new Date()).getFullYear() }} Room Choice. All Rights Reserved
          </div>
          <v-spacer />
          <div
            v-if="socialsFilled.length"
            class="social-list"
          >
            <div
              v-for="(social, idx) in socialsFilled"
              :key="idx"
              class="social-list__item"
            >
              <a
                :href="social.link"
                target="_blank"
              >
                <img
                  :src="require(`@/assets/svg/${social.icon}.svg`)"
                  alt=""
                >
              </a>
            </div>
          </div>
        </v-layout>
      </v-container>
    </footer>

    <auth-modal />
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator'
import { MediaItem } from '../../middleware/MediaItem'
import { Swiper, SwiperSlide } from 'swiper/vue'

import MediaView from '@/components/common/MediaView.vue'
import AuthModal from '../auth/AuthModal.vue'
import PageLayoutIntroText from './PageLayoutIntroText.vue'
import { authService } from '../../services/authServiceInstance'

@Component({
  components: {
    MediaView,
    AuthModal,
    Swiper,
    SwiperSlide,
    PageLayoutIntroText,
  },
  emits: ['action']
})

export default class PageLayout extends Vue {
  @Prop({ type: Boolean }) loaded!: boolean
  @Prop({ type: Array }) media!: MediaItem[]
  @Prop({ type: String }) title!: string
  @Prop({ type: String }) text!: string
  @Prop({ type: String }) buttonText!: string
  @Prop({ type: Boolean }) placeTextBottom!: boolean
  @Prop({ type: Array }) socials!: { type: string, icon: string, link: string }[]

  get organization () {
    return this.$store.getters['organization/organization']
  }

  get organizationLoaded () {
    return this.$store.getters['organization/organizationLoaded']
  }

  @Watch('organizationLoaded')
  onOrganizationLoad () {
    setTimeout(() => {
      this.checkCurrentSlide()
    })
  }

  currentSlide = 0

  swiperOptions = {
    loop: true,
    autoplay: {
      delay: 5000,
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
      clickable: true,
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
  }

  housingLink = {
    type: 'housing',
    icon: 'housingLogo',
    link: 'https://www.hud.gov/program_offices/fair_housing_equal_opp',
  }

  domainAliases = {}

  get socialsFilled () {
    return [this.housingLink, ...this.socials]
  }

  get showGallery (): boolean {
    return this.media.length > 1
  }

  getHomeUrl () {
    const { name, params } = this.$route
    if (name === 'Property' && params?.id) { return `/new/property/${params?.id}` }

    return '/'
  }

  openProfile () {
    this.$router.push('/tenant-profile/user-reservations/reservations')
  }

  openAuthModal (type) {
    this.$store.dispatch('ui/toggleAuthModal', {
      open: true,
      type,
    })
  }

  checkUserRole (role: string): boolean {
    // TODO Probably incorrect place for this check.
    if (!authService.user) {
      return
    }
    return authService.hasRole(role)
  }

  checkCurrentSlide () {
    const swiper = (this.$refs as any).mySwiper && ((this.$refs as any).mySwiper.$el as any).swiper
    if (!swiper) return

    swiper.on('slideChange', (val) => {
      this.currentSlide = swiper.activeIndex
    })
  }

  parseDomainAliases () {
    const { VUE_APP_DOMAIN_ALIASES } = process.env
    const aliases = VUE_APP_DOMAIN_ALIASES?.split(',') || []
    aliases.forEach((alias) => {
      const [vanityUrl, mainUrl, propertyId] = alias.split('>')
      this.domainAliases[propertyId] = {
        mainUrl,
        vanityUrl,
      }
    })
  }

  mounted () {
    this.checkCurrentSlide()
    this.parseDomainAliases()
  }

  logout () {
    authService.logout()
  }
}
</script>

<style lang="scss" src="@/assets/styles/page-layout.scss"></style>
